/**
 * SpanTree - Hierarchical span viewer within a trace
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { TYPE_ICONS, TYPE_COLORS, formatDuration } from './spanConstants';
import { useTraceSpans } from '../hooks/useTraces';
import { SmartContent } from './SmartContent';
import type { SpanPublic } from '@/types/traces';
import { useI18n } from '@/i18n';

interface SpanTreeProps {
  traceId: string;
}

export function SpanTree({ traceId }: SpanTreeProps) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const { data, isLoading } = useTraceSpans(traceId, { size: 100 });

  if (isLoading) {
    return <div className="text-xs text-gray-500 py-2">{t.loadingSpans}</div>;
  }

  if (!data || data.content.length === 0) {
    return <div className="text-xs text-gray-400 py-2">{t.noSpans}</div>;
  }

  // Build tree structure
  const spanMap = new Map<string, SpanPublic>();
  const rootSpans: SpanPublic[] = [];
  const childrenMap = new Map<string, SpanPublic[]>();

  for (const span of data.content) {
    spanMap.set(span.id, span);
    if (!span.parent_span_id) {
      rootSpans.push(span);
    } else {
      const siblings = childrenMap.get(span.parent_span_id) || [];
      siblings.push(span);
      childrenMap.set(span.parent_span_id, siblings);
    }
  }

  // Sort by start_time
  const sortByTime = (a: SpanPublic, b: SpanPublic) =>
    new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
  rootSpans.sort(sortByTime);
  childrenMap.forEach(list => list.sort(sortByTime));

  const truncated = data.total > data.content.length;

  return (
    <div className="space-y-0.5">
      {rootSpans.map(span => (
        <SpanNode key={span.id} span={span} childrenMap={childrenMap} depth={0} />
      ))}
      {truncated && (
        <div className="text-[10px] text-amber-600 bg-amber-50 rounded px-2 py-1 mt-1">
          {t.showingSpans.replace('{shown}', String(data.content.length)).replace('{total}', String(data.total))}
        </div>
      )}
    </div>
  );
}


function SpanNode({
  span,
  childrenMap,
  depth,
}: {
  span: SpanPublic;
  childrenMap: Map<string, SpanPublic[]>;
  depth: number;
}) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const [expanded, setExpanded] = useState(depth < 2);
  const [showDetail, setShowDetail] = useState(false);
  const childSpans = childrenMap.get(span.id) || [];
  const hasChildren = childSpans.length > 0;

  const spanType = (span.type || 'general') as string;
  const Icon = TYPE_ICONS[spanType] || TYPE_ICONS.general;
  const colorClass = TYPE_COLORS[spanType] || TYPE_COLORS.general;
  const durationMs = span.duration;

  return (
    <div style={{ marginLeft: depth * 16 }}>
      {/* Span row */}
      <div
        className="flex items-center gap-1.5 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer text-xs group"
        onClick={() => setShowDetail(!showDetail)}
      >
        {/* Expand toggle */}
        {hasChildren ? (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(!expanded); }}
            className="p-0.5 hover:bg-gray-200 rounded"
          >
            {expanded
              ? <ChevronDown className="w-3 h-3 text-gray-400" />
              : <ChevronRight className="w-3 h-3 text-gray-400" />
            }
          </button>
        ) : (
          <span className="w-4" />
        )}

        {/* Type badge */}
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${colorClass}`}>
          <Icon className="w-3 h-3" />
          {spanType}
        </span>

        {/* Name */}
        <span className="font-mono truncate flex-1 text-gray-800">{span.name}</span>

        {/* Model (if LLM) */}
        {span.model && (
          <span className="text-[10px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {span.model}
          </span>
        )}

        {/* Duration */}
        {durationMs != null && (
          <span className="text-gray-400 whitespace-nowrap">
            {formatDuration(durationMs)}
          </span>
        )}

        {/* Cost */}
        {span.total_estimated_cost != null && (
          <span className="text-gray-400 whitespace-nowrap">
            ${span.total_estimated_cost.toFixed(4)}
          </span>
        )}
      </div>

      {/* Span detail (inline) */}
      {showDetail && (
        <div className="ml-8 mb-2 p-2 bg-gray-50 rounded text-xs space-y-2">
          {span.input != null && (
            <div>
              <p className="font-medium text-gray-600 mb-1">{t.spanInput}</p>
              <SmartContent data={span.input} compact />
            </div>
          )}
          {span.output != null && (
            <div>
              <p className="font-medium text-gray-600 mb-1">{t.spanOutput}</p>
              <SmartContent data={span.output} compact />
            </div>
          )}
          {span.usage && (
            <div className="flex gap-3 text-gray-500">
              {span.usage.prompt_tokens != null && <span>{t.tokenPrompt} {span.usage.prompt_tokens}</span>}
              {span.usage.completion_tokens != null && <span>{t.tokenCompletion} {span.usage.completion_tokens}</span>}
              {span.usage.total_tokens != null && <span>{t.tokenTotal} {span.usage.total_tokens}</span>}
            </div>
          )}
        </div>
      )}

      {/* Children */}
      {expanded && childSpans.map(child => (
        <SpanNode key={child.id} span={child} childrenMap={childrenMap} depth={depth + 1} />
      ))}
    </div>
  );
}
